import { useState } from "react";
import { Box, Textarea, SimpleGrid, Text, Flex, MultiSelect, Group, Stack } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import {
  getSubIndustriesByIndustry,
  getCollaborationIntentOptions,
  getWhoAmIOptions,
  getWhoAmIDescription,
} from "@/components/shared/constants/general.const";

interface BusinessOverviewProps {
  formData: {
    companyDescription: string;
    industryCategory: string[];
    subIndustry: string[];
    whoAmI: string[];
    collaborationIntent: string[];
  };
  updateForm: (field: string, value: any) => void;
  industryOptions: { value: string; label: string }[];
  disabled?: boolean;
}

export default function BusinessOverview({
  formData,
  updateForm,
  industryOptions,
  disabled,
}: BusinessOverviewProps) {
  const [whoAmIHoverValue, setWhoAmIHoverValue] = useState<string | null>(null);

  // Get sub-industry options based on all selected industries (merged + deduplicated)
  const subIndustryOptions =
    formData.industryCategory.length > 0
      ? formData.industryCategory
          .flatMap((ind) => getSubIndustriesByIndustry(ind))
          .filter((opt, i, arr) => arr.findIndex((o) => o.value === opt.value) === i)
      : [];

  // Clear sub-industry when industry changes
  const handleIndustryChange = (values: string[]) => {
    updateForm("industryCategory", values);
    updateForm("subIndustry", []); // Reset sub-industries when industry changes
  };

  return (
    <Box>
      {/* Company Description */}
      <Box mb="md">
        <Text fz="sm" fw={500} mb={4}>
          Company Description
          <Text span c="red" fz="lg">
            {" "}
            *
          </Text>
        </Text>
        <Textarea
          placeholder="Describe your company, what you do, your expertise, and what makes you unique..."
          autosize
          minRows={2}
          maxLength={800}
          value={formData.companyDescription}
          onChange={(e) => updateForm("companyDescription", e.currentTarget.value)}
          disabled={disabled}
          error={formData.companyDescription.length > 800 ? "Description cannot exceed 800 characters" : null}
        />
        <Flex justify="space-between" mt={4}>
          <Text fz="xs" c="slate.6">
            Describe your company, expertise, and what makes you unique.
          </Text>
          <Text fz="xs" c="orange">
            {formData.companyDescription.length} / 800 characters
          </Text>
        </Flex>
      </Box>

      {/* Industry Category & Sub-industry */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="xl">
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Industry Category
              <Text span c="red" fz="lg">
                {" "}
                *
              </Text>
            </Text>
          }
          placeholder="Select industries"
          data={industryOptions}
          value={formData.industryCategory || []}
          onChange={handleIndustryChange}
          searchable
          disabled={disabled}
          clearable
        />
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Sub-industry (Optional)
            </Text>
          }
          placeholder={formData.industryCategory.length > 0 ? "Select sub-industries" : "Select industry first"}
          data={subIndustryOptions}
          value={formData.subIndustry || []}
          onChange={(value) => updateForm("subIndustry", value)}
          searchable
          disabled={disabled || formData.industryCategory.length === 0}
          clearable
          maxValues={5}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="xl">
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Who am I?
              <Text span c="red" fz="lg">
                {" "}
                *
              </Text>
            </Text>
          }
          description="Hover a role in the list to see a short explanation (inside the dropdown)"
          placeholder="Select your role"
          data={getWhoAmIOptions()}
          value={formData.whoAmI || []}
          onChange={(value) => updateForm("whoAmI", value)}
          searchable
          disabled={disabled}
          clearable
          maxDropdownHeight={420}
          onDropdownClose={() => setWhoAmIHoverValue(null)}
          renderOption={({ option, checked }) => {
            const detail = getWhoAmIDescription(option.value);
            const showDetail = detail && whoAmIHoverValue === option.value;
            return (
              <Box
                w="100%"
                onMouseEnter={() => setWhoAmIHoverValue(option.value)}
                onMouseLeave={() => setWhoAmIHoverValue((prev) => (prev === option.value ? null : prev))}
              >
                <Group gap={10} wrap="nowrap" align="flex-start" w="100%" py={showDetail ? 8 : 4}>
                  <Box w={20} miw={20} style={{ display: "flex", justifyContent: "center", flexShrink: 0, paddingTop: 2 }}>
                    {checked ? <IconCheck size={14} stroke={2.5} style={{ opacity: 0.85 }} /> : null}
                  </Box>
                  <Stack gap={showDetail ? 6 : 0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={600} lh={1.35}>
                      {option.label}
                    </Text>
                    {showDetail ? (
                      <Text size="xs" c="dimmed" lh={1.5} style={{ whiteSpace: "normal" }}>
                        {detail}
                      </Text>
                    ) : null}
                  </Stack>
                </Group>
              </Box>
            );
          }}
        />
        <MultiSelect
          label={
            <Text fz="sm" fw={500}>
              Collaboration Intent (Optional)
            </Text>
          }
          description="Select the types of collaboration you're looking for"
          placeholder="Select collaboration types"
          data={getCollaborationIntentOptions()}
          value={formData.collaborationIntent || []}
          onChange={(value) => updateForm("collaborationIntent", value)}
          searchable
          disabled={disabled}
          clearable
          maxValues={5}
        />
      </SimpleGrid>
    </Box>
  );
}
